<template>
  <span
    class="ui-categorybadge"
    :style="computedStyle"
    @click="$emit('edit')"
    >
    <span v-if="text && !iconLeft" v-text="text"/>
    <slot v-if="!iconLeft"/>
    <i
      v-if="svg"
      :class="svg"
      class="fa text-slate-500/40 cursor-pointer align-middle"
      @click="$emit('remove')"/>
    <slot v-if="iconLeft"/>
    <span v-if="text && iconLeft" @click="$emit('edit')" v-text="text"/>
    <i v-if="editable" class="fa fa-times text-gray-700 cursor-pointer pl-2 align-middle" @click="$emit('remove')"/>
    <i v-if="addable" class="fa fa-plus text-gray-700 cursor-pointer pl-2 align-middle" @click="$emit('add')"/>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'

defineEmits(['remove','add','edit'])
const props = defineProps<{
  svg?: string|object
  text?: string
  editable?: boolean
  addable?: boolean
  iconLeft?: boolean
  colored?: boolean
}>();

// Helpers to generate varied, readable colors
const hashString = (str: string) => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
  }
  return Math.abs(hash);
}

const hslToRgb = (h: number, s: number, l: number) => {
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r1 = 0, g1 = 0, b1 = 0;
  if (0 <= h && h < 60) { r1 = c; g1 = x; b1 = 0; }
  else if (60 <= h && h < 120) { r1 = x; g1 = c; b1 = 0; }
  else if (120 <= h && h < 180) { r1 = 0; g1 = c; b1 = x; }
  else if (180 <= h && h < 240) { r1 = 0; g1 = x; b1 = c; }
  else if (240 <= h && h < 300) { r1 = x; g1 = 0; b1 = c; }
  else { r1 = c; g1 = 0; b1 = x; }
  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const b = Math.round((b1 + m) * 255);
  return { r, g, b };
}

const relLuminance = (r: number, g: number, b: number) => {
  const toLin = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const R = toLin(r), G = toLin(g), B = toLin(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

const contrastRatio = (bg: { r: number, g: number, b: number }, fg: { r: number, g: number, b: number }) => {
  const L1 = relLuminance(bg.r, bg.g, bg.b);
  const L2 = relLuminance(fg.r, fg.g, fg.b);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}

const DARK_RGB = { r: 17, g: 24, b: 39 }; // #111827
const WHITE_RGB = { r: 255, g: 255, b: 255 }; // #ffffff

const computedStyle = computed(() => {
  const styling = {} as any;

  if (props.colored) {
    const seed = hashString(props.text || '');
    const hue = seed % 360; // full hue range
    const sat = 40 + (seed % 35); // 40–74% saturation for variety without being too vivid
    let light = 74 + (Math.floor(seed / 360) % 16); // 74–90% lightness, pastel-ish for default readability

    let bgRgb = hslToRgb(hue, sat, light);
    let contrastDark = contrastRatio(bgRgb, DARK_RGB);
    let contrastWhite = contrastRatio(bgRgb, WHITE_RGB);

    let useDarkText = contrastDark >= contrastWhite;
    const contrastBest = Math.max(contrastDark, contrastWhite);

    // Ensure good readability: target >= 4.5:1; adjust lightness if needed
    if (contrastBest < 4.5) {
      if (useDarkText) {
        // Make background lighter to increase contrast with dark text
        light = Math.min(92, Math.max(light, 88));
      } else {
        // Make background darker to increase contrast with white text
        light = Math.max(28, Math.min(light, 40));
      }
      bgRgb = hslToRgb(hue, sat, light);
      contrastDark = contrastRatio(bgRgb, DARK_RGB);
      contrastWhite = contrastRatio(bgRgb, WHITE_RGB);
      useDarkText = contrastDark >= contrastWhite;
    }

    styling.backgroundColor = `hsl(${hue}, ${sat}%, ${light}%)`;
    styling.color = useDarkText ? '#111827' : '#ffffff';
  }

  return styling;
})
</script>
